import { toValue, provide } from "vue";

import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionValuesRecord,
  type InjectionKeysRecord
} from "@mcwd/util-vue";

import { useMarketoFormInternals } from "./use-marketo-form-instance.js";
import { isNil } from "@mcwd/typescript-type-guards";

import { type FormWidgetData } from "../../widgetDataTs.js";

import { HiddenFieldValueBuilder } from "../form-logic/hidden-field-calculations/index.js";
import type { AnyFormSettingsKey } from "../form-settings/form-experience/form-settings-keys.js";
import { useFormSettings } from "./use-form-settings.js";
import { useSiteLanguage } from "./use-site-language.js";
import { useFormLogger } from "./use-form-logger.js";
import type { SiteCountryCodeUppercase } from "../../types-and-definitions/locale-defs/Locales.js";


type UseHiddenFieldInitializerInjectionValues = InjectionValuesRecord<{
  setHiddenFields: () => void;
  hiddenFieldValueBuilder: HiddenFieldValueBuilder;
}>;

type UseHiddenFieldInitializerInjectionKeys = InjectionKeysRecord<UseHiddenFieldInitializerInjectionValues>;

const UseHiddenFieldInitializerInjectionKeys = CreateInjectionKeysRecord<UseHiddenFieldInitializerInjectionKeys>({
  setHiddenFields: "setHiddenFields",
  hiddenFieldValueBuilder: "hiddenFieldValueBuilder",
});

export function initHiddenFieldInitializer(options: {
  formSettingsKey: AnyFormSettingsKey;
  widgetData: FormWidgetData,
  leadSource?: string;
  customDocumentDownloadURL?: string | undefined
}) {
  if (checkValuesAlreadyProvided(UseHiddenFieldInitializerInjectionKeys)) {
    return;
  }
  const widgetData = options.widgetData;
  const leadSource = options.leadSource;
  const formSettingsKey = options.formSettingsKey;

  const { FormSettings } = useFormSettings({ callerLabel: "hiddenFieldsInitializer" });
  const { siteCountryCode, localeUrlPrefix } = useSiteLanguage({ callerLabel: "use-hidden-field-initializer" });

  const customDocumentDownloadURL = options.customDocumentDownloadURL;
  const hiddenFieldValueBuilder = new HiddenFieldValueBuilder({
    widgetData,
    leadSource,
    formSettingsKey,
    formTrackingName: FormSettings.formTrackingName,
    siteCountryCode: (siteCountryCode.toUpperCase() as SiteCountryCodeUppercase),
    localeUrlPrefix,
    customDocumentDownloadURL
  });
  provide(
    UseHiddenFieldInitializerInjectionKeys.hiddenFieldValueBuilder,
    hiddenFieldValueBuilder
  );
}

export function useHiddenFieldInitializer({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  const { logger } = useFormLogger();
  try {
    callerLabel ??= "useHiddenFieldInitializer";
    const { MarketoFormObj } = useMarketoFormInternals({ callerLabel });

    const hiddenFieldValueBuilder = injectRequiredWithSelf(UseHiddenFieldInitializerInjectionKeys.hiddenFieldValueBuilder);

    return Object.freeze({
      hiddenFieldValueBuilder,
      setHiddenFields() {
        if (isNil(MarketoFormObj.value)) {
          throw new Error("MarketoFormObj is Nil!");
        }
        const values = toValue(hiddenFieldValueBuilder.hiddenFieldValuesObject);
        MarketoFormObj.value.setValues(values);
      }
    });
  }
  catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? 'unknown'}`);
    throw err;
  }
}